import { faChevronDown } from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { motion } from "framer-motion";
import { NextSeo } from "next-seo";
import { Col, Row } from "react-styled-flexboxgrid";
import styled from "styled-components";
import { StyledHeroRow } from ".";
import Public from "../Layouts/Public";
import Heading from "../components/Heading";
import PageSection, { StyledSectionHeading } from "../components/PageSection";
import Spacer from "../components/Spacer";
import { siteTitle } from "../data/PublicSiteData";
import { trackPageView } from "../includes/analytics";
import { scrollTo } from "../includes/utils";
import { device } from "../theme/Breakpoints";
import { Color } from "../theme/Color";
import { FontSizes } from "../theme/FontSize";

const HeroSection = styled(PageSection)`
  background: ${Color.White} url("/img/hero-images/about-hero-bg.webp")
    no-repeat bottom center;
  background-size: cover;
`;

const HeroTitle = styled.h1`
  font-size: ${FontSizes[3]};
  font-weight: bold;
  text-transform: uppercase;
  line-height: 150%;
  text-align: left;

  @media ${device.mdOnly} {
    font-size: ${FontSizes[3]};
  }

  @media ${device.lgAndWider} {
    font-size: ${FontSizes[5]};
    margin-bottom: 1rem;
  }
`;

const GoToNextSection = styled.div`
  margin-top: -75px;
  width: 100%;
  text-align: center;
`;

const StyledH2 = styled.h2`
  font-size: ${FontSizes[3]};
  font-weight: bold;
  line-height: 160%;
  text-align: left;
  margin-bottom: 2rem;
`;

const StyledH3 = styled.h3`
  font-size: ${FontSizes[2]};
  line-height: 160%;
  text-align: left;
`;

const Home = () => {
  const heroContentVariants = {
    initial: { opacity: 0, y: "10vh" },
    animate: {
      opacity: 1,
      y: 0,
    },
  };

  trackPageView("/");

  return (
    <>
      <NextSeo
        title={`About | ${siteTitle}`}
        description="Valmeego is a new way for destinations and visitors to connect directly, combined with a user-friendly multi-purpose tool for researching, planning and booking any trip."
      />
      {/* Hero Section */}
      <HeroSection fullHeight={true}>
        <StyledHeroRow middle="xs">
          <Col xs={false} sm={4} md={4} lg={4}>
            <Row middle="xs">
              <Spacer all={2}>
                <img
                  src={`/img/app-screenshots/ask-a-local-ireland-map.png`}
                  alt="Valmeego.com - Locally and beyond"
                  style={{ width: "100%", maxWidth: "338px", height: "auto" }}
                />
              </Spacer>
            </Row>
          </Col>
          <Col xs={1} sm={false}></Col>
          {/* Homepage text - Everything but mobile */}
          <Col xs={10} sm={6} md={8} lg={8}>
            <Row>
              <HeroTitle
                as={motion.h1}
                variants={heroContentVariants}
                initial="initial"
                animate="animate"
                transition={{
                  delay: 0.2,
                  duration: 0.4,
                  type: "spring",
                  damping: 12,
                  mass: 1,
                }}
              >
                Connect directly with knowledgeable local advisors and book
                directly with recommended local businesses.
              </HeroTitle>
            </Row>
            <Row>
              <Col xs={false} sm={12}>
                <StyledH2
                  as={motion.h2}
                  variants={heroContentVariants}
                  initial="initial"
                  animate="animate"
                  transition={{
                    delay: 0.4,
                    duration: 0.4,
                    type: "spring",
                    damping: 12,
                    mass: 1,
                  }}
                >
                  The Valmeego platform is a gateway to everything a community
                  offers, whether you are living in that community, considering
                  a visit, or just passing through.
                </StyledH2>
              </Col>
            </Row>
            <Row>
              <Spacer bottom={3}>
                <StyledH3
                  as={motion.h2}
                  variants={heroContentVariants}
                  initial="initial"
                  animate="animate"
                  transition={{
                    delay: 0.6,
                    duration: 0.4,
                    type: "spring",
                    damping: 12,
                    mass: 1,
                  }}
                >
                  With Valmeego, you'll always know what's happening or
                  available in any destination. Local events, community
                  gatherings, festivals, and all those interesting local
                  activities which provide you with an authentic experience are
                  now easily found. A few simple clicks connect you directly
                  with local communities, helpful locals, and local businesses.
                </StyledH3>
              </Spacer>
            </Row>
          </Col>
          <Col xs={1} sm={false}></Col>
        </StyledHeroRow>
        <GoToNextSection>
          <FontAwesomeIcon
            icon={faChevronDown}
            size="2x"
            onClick={() => {
              scrollTo("how-will-it-work");
            }}
          />
        </GoToNextSection>
      </HeroSection>

      <PageSection
        backgroundColor={Color.White}
        width="1200px"
        id="how-will-it-work"
      >
        <Row center="xs" middle="xs">
          <Col
            xs={10}
            md={8}
            style={{ color: Color.Grey2, marginBottom: "5rem" }}
          >
            <StyledSectionHeading>
              Become a member of a Global, local community
            </StyledSectionHeading>
            <p>
              Everyone is a local somewhere and Valmeego’s local members share
              their insider knowledge and recommendations, helping to drive
              community growth and enhance the experience of their area.
            </p>
            <p>
              The essence of Valmeego is about enriching communities, sharing
              experiences and knowledge, and forging lasting connections between
              people.
            </p>
            <p>
              Our platform unites locals, travellers, destinations, and
              businesses in an engaging, indispensable tool for everyday life.
              Whether at home, abroad or looking for your next destination
              Valmeego provides the simple means of connecting directly with
              locals, local advisors, and local businesses.
            </p>
            <Heading
              size={6}
              centered
              margin="5rem 0 2rem 0"
              fontColor={Color.Orange5}
            >
              <span style={{ fontWeight: "normal" }}>CORE VALUES</span>
            </Heading>
            <p>
              <b>Our Core Values:</b> Unforgettable experiences and memories are
              primarily the result of positive personal interactions.
            </p>
            <p>
              These interactions are enjoyable and beneficial but particularly
              so in the context of hospitality and travel when we are looking to
              experience the best a destination has to offer.
            </p>
            <p>
              Valmeego will create an environment where members are friendly,
              courteous, and respectful in their manner as well as fair and
              honest in their interactions with each other.
            </p>
            <p>
              These values are timeless and are honoured across peoples and
              cultures around the world.
            </p>
            <Heading
              centered
              size={4}
              fontColor={Color.Orange5}
              margin="5rem 0 2rem 0"
            >
              All Members
            </Heading>
            <p>
              <b>All Members</b> receive a free user friendly, multi-purpose
              tool for researching, planning, organising, and booking any trip
              or event using the benefit of local knowledge, local advice and
              recommendations from family, friends and the wider Valmeego
              community. Every member can look for and receive personalised
              recommendations specifically suited to their requirements and
              preferences.
            </p>
            <Heading
              centered
              size={4}
              fontColor={Color.Orange5}
              margin="5rem 0 2rem 0"
            >
              Local Advisors (Ameegos)
            </Heading>
            <p>
              <b>Local advisors</b> want you to experience Ireland in a way most
              visitors miss out on. They have a pride of place and a depth of
              knowledge that cannot be equalled and they want you to receive the
              benefit of their help as you plan your trip. Immerse yourself in
              our history, culture,heritage, music, stories and hidden places by
              connecting with locals who want you to truly experience Ireland.
              Real people talking to each other. Real places without the crowds.
              Real stories rooted in the community. Real impact for communities
              who appreciate you.
            </p>
            <Heading
              centered
              size={4}
              fontColor={Color.Orange5}
              margin="5rem 0 2rem 0"
            >
              Businesses
            </Heading>
            <p>
              <b>For businesses</b>, Valmeego provides a direct channel to reach
              locals and travellers. Increase your visibility, attract, and keep
              more customers, grow your brand, and achieve more direct bookings.
            </p>
            <Heading
              centered
              size={4}
              fontColor={Color.Orange5}
              margin="5rem 0 2rem 0"
            >
              Destinations
            </Heading>
            <p>
              <b>For destinations</b>, Valmeego offers a platform to showcase
              your destinations unique attractions and experiences. Engage with
              travellers and locals, promote local events, and boost tourism in
              your area, all while emphasising what makes your community
              special.
            </p>
            <p>
              The Valmeego platform is designed to showcase the unique beauty
              and diversity of each destination and its local community. Our
              members can use our platform to give much more to their own
              locality and then in due course receive much more when they travel
              to other destinations. Journeys can be much more than just ticking
              off landmarks and racing on to the next photo. Our members will
              have a more immersive experience that leaves a lasting and
              positive impact on them and the communities they visit
            </p>
          </Col>
        </Row>
      </PageSection>
    </>
  );
};

Home.Layout = Public;
Home.Title = "Valmeego - Locally and beyond";

export default Home;
